import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { createPaymentLink, getCreditValidator } from "../hooks/local/userReducer";
import close from "../assets/svg-icons/closeBrand.svg";

const PaymentModal = ({
  isOpen,
  onClose,
  currency,
  totalPrice,
  walletBalance,
  virtualAccount,
  onPaymentSuccess,
}) => {
  const dispatch = useDispatch();
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [confirmPaymentLoading, setConfirmPaymentLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [paymentLink, setPaymentLink] = useState("");
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [amountValue, setAmountValue] = useState("");
  const [showSupportModal, setShowSupportModal] = useState(false);

  const dollarPaymentLink = useFormik({
    initialValues: {
      amount: amountValue,
    },
    validationSchema: Yup.object({
      amount: Yup.string().required("Amount cannot be empty"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { amount } = values;
      const { payload } = await dispatch(createPaymentLink({ amount }));

      const payment_link = payload?.payment_link;
      setPaymentLink(payment_link);

      if (payload.statuscode === "00") {
        setLinkGenerated(true);
        resetForm();
        setAmountValue("");
      }
    },
  });

  const handleAmountChange = (e) => {
    setAmountValue(e.target.value);
    dollarPaymentLink.setFieldValue("amount", e.target.value);
  };

  const handleConfirmPayment = async () => {
    if (!paymentConfirmed) {
      toast.error("Please confirm that you have made the payment.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return;
    }

    setConfirmPaymentLoading(true);
    try {
      const { payload } = await dispatch(getCreditValidator({ amount: totalPrice }));

      if (payload && payload?.statuscode === "00") {
        const userBalance =
          payload?.pref_deno === "naira" ? payload?.balance : payload?.dollar_balance;

        if (totalPrice > userBalance) {
          setConfirmPaymentLoading(false);
          setRetryCount((prev) => prev + 1);
          if (retryCount + 1 >= 2) {
            setShowSupportModal(true);
          } else {
            toast.error(
              `Payment unsuccessful. Insufficient balance: ${currency === "USD" ? "USD" : "₦"} ${userBalance.toLocaleString()}. Required: ${currency === "USD" ? "USD" : "₦"} ${totalPrice.toLocaleString()}.`,
              {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                theme: "colored",
              }
            );
          }
        } else {
          setShowSupportModal(false);
          setRetryCount(0);
          setPaymentConfirmed(false);
          setLinkGenerated(false);
          setPaymentLink("");
          toast.success("Payment confirmed successfully!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            theme: "colored",
          });
          onPaymentSuccess();
          onClose();
        }
      } else {
        throw new Error("Payment verification failed");
      }
    } catch (error) {
      setRetryCount((prev) => prev + 1);
      setConfirmPaymentLoading(false);
      if (retryCount + 1 >= 2) {
        setShowSupportModal(true);
      } else {
        toast.error("Payment verification failed. Please try again.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          theme: "colored",
        });
      }
    }
  };

  const handleSupportModalClose = () => {
    setShowSupportModal(false);
    setLinkGenerated(false);
    setPaymentLink("");
    setAmountValue("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[#141D26] rounded-lg w-full max-w-md max-h-[80vh] overflow-auto">
          <div className="flex justify-between items-center p-6 border-b border-[#c4c4c432] dark:border-[#c4c4c432]">
            <h2 className="text-sm font-medium text-primaryBlue">
              {currency === "USD" ? "Dollar" : "Naira"} Account Details
            </h2>
            <img
              src={close}
              onClick={onClose}
              className="h-5 cursor-pointer"
              alt="Close"
            />
          </div>
          <div className="p-6 text-sm grid gap-4">
            {virtualAccount ? (
              <>
                {currency === "USD" ? (
                  linkGenerated ? (
                    <div className="grid gap-4">
                      <input
                        type="text"
                        value={paymentLink}
                        disabled
                        className="w-full p-3 border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                      />
                      {paymentLink && (
                        <a
                          href={paymentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <button
                            className="w-full text-white bg-primaryBlue rounded p-4 text-xs hover:bg-blue-600"
                            onClick={onClose}
                          >
                            Make Payment
                          </button>
                        </a>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-2">
                        <p className="font-semibold">OPTION 1: Generate Payment Link</p>
                        <div>
                          <input
                            type="text"
                            placeholder="Enter amount"
                            value={amountValue}
                            onChange={handleAmountChange}
                            onBlur={dollarPaymentLink.handleBlur}
                            className="w-full p-3 border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md bg-white dark:bg-[#1f2a44] text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primaryBlue"
                          />
                          {dollarPaymentLink.errors.amount && dollarPaymentLink.touched.amount && (
                            <span className="text-red-500 text-xs mt-1">
                              {dollarPaymentLink.errors.amount}
                            </span>
                          )}
                        </div>
                        <button
                          className="w-full text-white bg-primaryBlue rounded p-4 text-xs hover:bg-blue-600"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            dollarPaymentLink.handleSubmit(e);
                          }}
                        >
                          Generate Payment Link
                        </button>
                      </div>
                      <div className="grid gap-2 mt-5 border-t border-[#c4c4c432] pt-5">
                        <p className="font-semibold">OPTION 2: Bank Transfer</p>
                        <p>
                          Transfer the exact amount to the account below and call{" "}
                          <a href="tel:09069434255" className="text-blue-500 underline">
                            09069434255
                          </a>{" "}
                          or send a mail with receipt of payment to{" "}
                          <a href="mailto:support@cloudvantage.com.ng" className="text-blue-500 underline">
                            support@cloudvantage.com.ng
                          </a>.
                        </p>
                        <p><span className="font-semibold">Bank name:</span> Guaranty Trust Bank</p>
                        <p><span className="font-semibold">Account number:</span> 0891825462</p>
                        <p><span className="font-semibold">Account name:</span> Cloudvantage Limited</p>
                      </div>
                    </>
                  )
                ) : (
                  <div className="grid gap-2">
                    <p className="font-semibold mb-5">Fund your wallet by making a bank transfer to account details below:</p>
                    <p><span className="font-semibold">Bank name:</span> Providus Bank</p>
                    <p><span className="font-semibold">Account number:</span> {virtualAccount.acct_num || ""}</p>
                    <p><span className="font-semibold">Account name:</span> {virtualAccount.acct_name || ""}</p>
                  </div>
                )}
                {!linkGenerated && (
                  <>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={paymentConfirmed}
                        disabled={confirmPaymentLoading}
                        onChange={(e) => setPaymentConfirmed(e.target.checked)}
                        className="h-5 w-5 text-primaryBlue border-gray-300 rounded focus:ring-primaryBlue"
                      />
                      <span>I can confirm I already made payment</span>
                    </label>
                    <button
                      className={`w-full text-white rounded p-4 text-xs flex justify-center items-center ${
                        retryCount >= 2 || confirmPaymentLoading ? "bg-gray-500 cursor-not-allowed" : "bg-primaryBlue hover:bg-blue-600"
                      }`}
                      onClick={handleConfirmPayment}
                      disabled={retryCount >= 2 || confirmPaymentLoading}
                    >
                      {confirmPaymentLoading ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                      ) : retryCount > 0 ? (
                        "Retry Payment"
                      ) : (
                        "Confirm Payment"
                      )}
                    </button>
                  </>
                )}
              </>
            ) : (
              <p>No account details available.</p>
            )}
          </div>
        </div>
      </div>

      {showSupportModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-primaryBlue mb-4">Payment Verification Failed</h2>
              <p className="text-sm">
                We could not verify your payment. Please contact our support team at{" "}
                <a href="tel:09069434255" className="text-blue-500 underline">
                  09069434255
                </a>{" "}
                or email{" "}
                <a href="mailto:support@cloudvantage.com.ng" className="text-blue-500 underline">
                  support@cloudvantage.com.ng
                </a>{" "}
                to confirm your payment.
              </p>
              <div className="flex justify-end mt-4">
                <button
                  className="text-white bg-primaryBlue rounded p-4 text-xs hover:bg-blue-600"
                  onClick={handleSupportModalClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentModal;